import { useRef } from "react";
import { ReactComponent as UploadIcon } from "../../assets/icons/form/upload-icon.svg";
import { useState, useEffect } from "react";

interface Props {
  label?: string;
  value?: File | null;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  labelClassName?: string;
  error?: boolean;
  imageUrl?: string;
  roundedImage?: boolean;
  accept?: string;
  /** When provided, a ✕ button is shown over the current image to remove it. */
  onClear?: () => void;
  /** Replace the default format and size lines in the empty box. */
  formatsHint?: string;
  sizeHint?: string;
}

export const FormUpload = ({
  label,
  value,
  onChange,
  labelClassName,
  error,
  imageUrl,
  roundedImage,
  accept = ".svg, .png, .jpg, .gif, .jpeg",
  onClear,
  formatsHint = "SVG, PNG, JPG or GIF",
  sizeHint = "(max, 800 X 800px)",
}: Props) => {
  /** Refs */
  const fileInputRef = useRef(null);
  /** State */
  const [imagePath, setImagePath] = useState<string | null>(null);

  const handleUploadClick = () => {
    if (fileInputRef?.current) {
      // @ts-ignore
      fileInputRef?.current?.click();
    }
  };

  useEffect(() => {
    if (value) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePath(reader.result as string);
      };
      reader.readAsDataURL(value);
      return;
    }
    if (imageUrl) {
      return setImagePath(imageUrl);
    }
    setImagePath(null);
  }, [value, imageUrl]);

  return (
    <div className="mb-4">
      <p
        className={`font-satoshi text-dark text-sm font-medium mb-3 ${labelClassName}`}
      >
        {label}
      </p>
      <div
        onClick={handleUploadClick}
        className={`w-full py-[29px] flex flex-col justify-center items-center border border-dashed ${
          error ? "border-red-base border-2" : "border-primary-base border"
        } rounded bg-sky-lighter cursor-pointer`}
      >
        {imagePath ? (
          <div className="relative">
            <img
              src={imagePath}
              alt="uploaded"
              className={`w-max[200px] h-[120px] object-contain ${
                roundedImage ? "rounded-full" : ""
              }`}
            />
            {onClear && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                aria-label="Remove image"
                className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white shadow border border-gray-200 text-gray-500 hover:text-red-600"
              >
                ✕
              </button>
            )}
          </div>
        ) : (
          <>
            <UploadIcon className="mb-4" />
            <div className="flex flex-row items-center">
              <p className="font-satoshi text-sm font-medium text-primary-base">
                Click to upload
              </p>
              <p className="font-satoshi text-sm font-medium text-gray-text ml-1">
                or drag and drop
              </p>
            </div>
            <p className="font-satoshi text-sm font-medium text-gray-text">
              {formatsHint}
            </p>
            <p className="font-satoshi text-sm font-medium text-gray-text">
              {sizeHint}
            </p>
          </>
        )}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept={accept}
          onChange={onChange}
        />
      </div>
    </div>
  );
};
